import { types, setFilter } from 'actions/search';
import {
  types as orgTypes,
  setOrgAiFilterSessionId,
} from 'actions/organisation';
import { sessionEnded } from 'actions/searchNLP';
import { getAISearchData, getAiSession } from 'selectors/searchNLP';
import { filtersMatchAIBaseline } from 'utils/aiSavedSearch';

/**
 * Before any search runs, end the AI session when current filters no longer match
 * the AI-applied baseline (so sparkle / Save Search stay in sync for every entry point).
 *
 * Each surface owns its own session, so this watches both entry points separately:
 *   - main search: SEARCH_PERFORM_SEARCH, comparing state.search.filters against
 *     the active session, and ending it on divergence
 *   - org profile: PERFORM_SEARCH / PERFORM_WORKITEM_SEARCH, comparing the shared
 *     org slice state.organisation.filters[orgId] against the session that slice
 *     applied, and clearing only that slice's id — never the global active session,
 *     which belongs to main search (SCIL-18950)
 * `filtersMatchAIBaseline` is shape-agnostic (fingerprints after stripping the
 * transient session id), so both slices reuse it directly.
 */
export const aiSearchBaselineMiddleware = store => next => action => {
  if (action.type === types.PERFORM_SEARCH) {
    const state = store.getState();
    const filters = state.search?.filters;
    const aiSearchData = getAISearchData(state);
    if (!filtersMatchAIBaseline(filters, aiSearchData)) {
      store.dispatch(sessionEnded('baseline-mismatch'));
      store.dispatch(setFilter('aiFilterSessionId', undefined));
    }
  }

  if (
    action.type === orgTypes.PERFORM_SEARCH ||
    action.type === orgTypes.PERFORM_WORKITEM_SEARCH
  ) {
    const state = store.getState();
    const organisationId = action.payload?.organisationId;
    const orgFilters = state.organisation?.filters?.[organisationId];
    const orgSessionId = orgFilters?.aiFilterSessionId;
    // Only act when this org slice actually carries an AI session — a plain
    // (non-AI) org search has no baseline to compare against.
    if (orgSessionId) {
      const orgAiSearchData = getAiSession(orgSessionId)(state)?.provenance;
      if (!filtersMatchAIBaseline(orgFilters, orgAiSearchData)) {
        store.dispatch(setOrgAiFilterSessionId(organisationId, undefined));
      }
    }
  }

  return next(action);
};
